import { type FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { theme } from "../../theme";
import { AddIcon } from "../atoms/icons/AddIcon";
import BodySmall from "../atoms/typography/BodySmall";
import Checkbox from "../atoms/inputs/Checkbox";

interface Tag {
  key: string;
  labelKey: string;
  selected: boolean;
}

interface Props {
  tags: Tag[];
  onToggle: (key: string) => void;
}

const TriggerButton = styled.button`
  padding: 2px 12px 2px 8px;
  border-radius: 12px;
  border: 1px solid ${theme.colors.textMuted};
  background-color: ${theme.colors.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;

  &:hover {
    border-color: ${theme.colors.primary};
  }
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 100;
  background-color: ${theme.colors.white};
  border-radius: 8px;
  padding: 8px;
  border: 1px solid ${theme.colors.grey05};
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 220px;
  white-space: nowrap;
`;

const OptionRow = styled.div`
  padding: 6px 4px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: ${theme.colors.hover};
  }
`;

const AddTagDropdown: FC<Props> = ({ tags, onToggle }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <TriggerButton onClick={() => setOpen(!open)}>
        <AddIcon color={theme.colors.primary} />
        <BodySmall fontweight={600} color={theme.colors.primary}>
          {t("invoices.addTag")}
        </BodySmall>
      </TriggerButton>
      {open && (
        <DropdownContainer>
          {tags.map((tag) => (
            <OptionRow key={tag.key} onClick={() => onToggle(tag.key)}>
              <Checkbox
                checked={tag.selected}
                onChange={() => onToggle(tag.key)}
                title={t(tag.labelKey)}
              />
            </OptionRow>
          ))}
        </DropdownContainer>
      )}
    </div>
  );
};

export default AddTagDropdown;
